import { faArrowLeft, faCloudExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { UserfulTooltip } from "userful-chronos-common-ui/dist";
import { ClusterDeviceInfo, IClusterInfo, IConstellationInfo } from "../../../model/CarbonExternalModels";
import { AppSliceActions } from "../../../store/app/appSlice";
import { selectDashboards, useCarbonDispatch, useCarbonSelector } from "../../../store/carbonStore";

import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import DevicesDropdown from "./devicesDropdown/DevicesDropdown";
import DeviceSystemInfo from "./DeviceSystemInfo";

import "./DevicePage.scss";
interface IProps {
    foundObject: { cluster: IClusterInfo; constellation: IConstellationInfo; device: ClusterDeviceInfo };
}

type TDevice = "SYSTEM_INFO" | "NETWORKING" | "GPU" | "CPU" | "STORAGE" | "CAPACITY";
type TDashboardView = Exclude<TDevice, "SYSTEM_INFO">;

const dashboardUriKeys: Record<TDashboardView, string> = {
    NETWORKING: "NETWORK",
    GPU: "GPU",
    CPU: "CPU",
    STORAGE: "STORAGE",
    CAPACITY: "CAPACITY",
};

const dashboardLabels: Record<TDashboardView, string> = {
    NETWORKING: "Networking",
    GPU: "GPU",
    CPU: "CPU",
    STORAGE: "Storage",
    CAPACITY: "Capacity",
};

export default function DevicePage({ foundObject }: IProps) {
    const [clusterView, setDeviceView] = useState<TDevice>("SYSTEM_INFO");

    const dispatch = useCarbonDispatch();

    const dashboards = useCarbonSelector(selectDashboards);
    const dashboard = dashboards[foundObject.cluster.id.value];

    const dashboardRef = useRef<HTMLIFrameElement | null>(null);
    const selectedDashboardView: TDashboardView | undefined = clusterView === "SYSTEM_INFO" ? undefined : clusterView;
    const dashboardUris = dashboard?.uris;
    const hasDashboardUrls = Object.values(dashboardUris ?? {}).some((uri) => uri.trim().length > 0);
    const dashboardTitle = selectedDashboardView
        ? `${foundObject.device.name} ${dashboardLabels[selectedDashboardView]} dashboard`
        : `${foundObject.device.name} dashboard`;

    let URI = "";
    let error = "";

    if (selectedDashboardView) {
        if (!dashboard) {
            error = "Critical error: no dashboards are available for this cluster.";
        } else if (!dashboardUris || !hasDashboardUrls) {
            error = "Critical error: current dashboard does not have any dashboard URLs.";
        } else {
            URI = dashboardUris[dashboardUriKeys[selectedDashboardView]]?.trim() ?? "";

            if (URI.length === 0) {
                error = `Dashboard URL is missing for the ${dashboardLabels[selectedDashboardView]} dashboard.`;
            }
        }
    }

    useEffect(() => {
        const iframe = dashboardRef.current;

        if (!iframe || URI.length === 0) {
            return;
        }

        iframe.onload = () => {
            iframe.contentWindow?.postMessage(
                {
                    Authorization: "Bearer " + getGlobalStates().keycloak.token,
                },
                "https://modjo-grafana.gcm2.userful.dev",
            );
        };

        return () => {
            iframe.onload = null;
        };
    }, [URI]);

    return (
        <div className="genericWrapper">
            <div className="actionsBar">
                <div className="cluster normalGap">
                    <UserfulTooltip title={"Return to "}>
                        <button
                            className="iconButton"
                            onClick={() => {
                                dispatch(AppSliceActions.setAppView({ type: "CLUSTER", id: foundObject.cluster.id }));
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    </UserfulTooltip>
                    <DevicesDropdown foundObject={foundObject} />
                    <div className="multiStateWrapper">
                        <button
                            className={`mSbutton ${clusterView === "SYSTEM_INFO" ? "active" : ""}`}
                            onClick={() => {
                                setDeviceView("SYSTEM_INFO");
                            }}
                        >
                            <p>System Info</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "NETWORKING" ? "active" : ""}`}
                            onClick={() => {
                                setDeviceView("NETWORKING");
                            }}
                        >
                            <p>Networking</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "GPU" ? "active" : ""}`}
                            onClick={() => {
                                setDeviceView("GPU");
                            }}
                        >
                            <p>GPU</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "CPU" ? "active" : ""}`}
                            onClick={() => {
                                setDeviceView("CPU");
                            }}
                        >
                            <p>CPU</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "STORAGE" ? "active" : ""}`}
                            onClick={() => {
                                setDeviceView("STORAGE");
                            }}
                        >
                            <p>Storage</p>
                        </button>
                        <button
                            className={`mSbutton ${clusterView === "CAPACITY" ? "active" : ""}`}
                            onClick={() => {
                                setDeviceView("CAPACITY");
                            }}
                        >
                            <p>Capacity</p>
                        </button>
                    </div>
                </div>
                <div className="cluster normalGap">
                    {/* <UserfulTooltip title={"Delete"}>
                        <button className="iconButton">
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                    </UserfulTooltip> */}
                </div>
            </div>
            {clusterView === "SYSTEM_INFO" && (
                <div className="device-body">
                    <DeviceSystemInfo foundObject={foundObject} />
                </div>
            )}
            {clusterView !== "SYSTEM_INFO" && URI.length !== 0 && (
                <iframe ref={dashboardRef} className="full-iframe-container" src={URI} title={dashboardTitle} />
            )}
            {clusterView !== "SYSTEM_INFO" && URI.length === 0 && (
                <div className="device-dashboard-error">
                    <FontAwesomeIcon icon={faCloudExclamation} />

                    <div className="device-dashboard-error-copy">
                        <h1>Oops! Something went wrong!</h1>

                        <p>{error}</p>
                    </div>
                </div>
            )}
        </div>
    );
}
